import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, Transition as _Transition, withCtx as _withCtx, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "snapshot-layer-title" }
const _hoisted_2 = ["src"]
const _hoisted_3 = { class: "countdown-number-wrapper lh-18 font-12" }
const _hoisted_4 = {
  key: 1,
  class: "countdown-number"
}
const _hoisted_5 = ["src"]
const _hoisted_6 = { class: "snapshot-layer-tips PingFangSC-Regular lh-24 font-14" }

import { ClickAction } from '@/data/report'
import useScreen from '@/hooks/use-screen'
import useSelect from '@/hooks/use-select'
import {
  namedImages,
  useCameraFrameStore,
  useConfAndQueryStore,
  useProductStore,
  useStackingStore
} from '@/store'
import { delay, throttle } from '@/utils'
import { isAndroid, isIOS } from '@/utils/device'
import { cropBase64Image } from '@/utils/image'
import { computed, nextTick, reactive, ref, watch } from 'vue'

// eslint-disable-next-line no-undef
const CUTTING_NUM = 3


export default /*@__PURE__*/_defineComponent({
  __name: 'SnapshotLayer',
  props: {
  visible: Boolean,
  snapshotData: Object as () => {
    base64?: string
    width?: string
    height?: string
  }
},
  emits: ['close'],
  setup(__props, { emit: __emit }) {

const emit = __emit
// eslint-disable-next-line no-undef
const props = __props
const { screenHeight } = useScreen()
const productStore = useProductStore()
const stackingStore = useStackingStore()
const { snapshot } = useCameraFrameStore()
const { report } = useConfAndQueryStore()
const { isFaceTryOn, isStackingsSelected } = useSelect()

const shown = ref(false)
const cutting = ref(false)
const cuttingWithTransition = ref(false)
const previewWrapper = ref<HTMLDivElement>()

const displaySecond = ref(CUTTING_NUM)
const corpedData = ref('')
const corpedUrl = ref('')

const isShort = computed(() => screenHeight.value < 700)

const title = computed(() => {
  const title = isStackingsSelected.value
    ? stackingStore.selectedStacking?.title
    : productStore.selectedProduct?.title

  const segs = (title || []).map(
    ({
      text,
      isAsciiOrExtended
    }: {
      text: string
      isAsciiOrExtended: boolean
    }) => ({
      text,
      typo: isAsciiOrExtended
        ? 'ABChanelCorpo-2022-Regular font-18'
        : 'PingFangSC-SemiBold font-16'
    })
  )

  if (productStore.selectedProduct?.skuId === 'H6951') {
    const [line1, ...line2] = segs
    return [[line1], line2]
  } else {
    return [segs]
  }
})

const actionValue = computed(() =>
  isStackingsSelected.value
    ? stackingStore.selectedStacking?.titleString
    : productStore.selectedProduct?.skuId
)

const tips = computed(() => {
  if (corpedUrl.value) {
    return '长按保存或分享图片'
  }
  if (isFaceTryOn.value) {
    return '将面部居于拍摄框中央'
  }
  return '将一只手置于拍摄框中央'
})

const touchState = reactive({
  isTouching: false,
  startTime: 0,
  isMoved: false
})

const resetTouchState = () => {
  Object.assign(touchState, {
    isTouching: false,
    startTime: 0,
    isMoved: false
  })
}

const onTouchStart = (e: TouchEvent) => {
  if (touchState.isTouching) return
  console.log('[[ Snapshot ] Longpress / TouchStart ]', e.timeStamp)
  touchState.isTouching = true
  touchState.startTime = e.timeStamp
}

const onTouchMove = () => {
  if (touchState.isTouching) {
    if (touchState.isMoved) return
    touchState.isMoved = true
  }
}

const onTouchEnd = (e: TouchEvent) => {
  if (touchState.isTouching) {
    console.log(
      '[[ Snapshot ] Longpress / TouchEnd ]',
      {
        ios: isIOS,
        moved: touchState.isMoved,
        duration: e.timeStamp - touchState.startTime
      },
      e.timeStamp
    )
    if (!touchState.isMoved) {
      if (e.timeStamp - touchState.startTime > 600) {
        report(ClickAction.LONGPRESS, actionValue.value)
      }
    }
  }
  resetTouchState()
}

const onTouchCancel = (e: TouchEvent) => {
  if (touchState.isTouching) {
    console.log(
      '[[ Snapshot ] Longpress / TouchCancel ]',
      {
        android: isAndroid,
        moved: touchState.isMoved,
        duration: e.timeStamp - touchState.startTime
      },
      e.timeStamp
    )
    if (!touchState.isMoved) {
      if (isAndroid && e.timeStamp === touchState.startTime) {
        report(ClickAction.LONGPRESS, actionValue.value)
      }
    }
  }
  resetTouchState()
}

const reset = () => {
  shown.value = false
  cutting.value = false
  displaySecond.value = CUTTING_NUM
}

const onClose = throttle(async () => {
  report(ClickAction.BACK, actionValue.value)
  reset()
  await nextTick()
  emit('close')
})

const requestSeconds = (s = 1) => {
  return new Promise<{ now: number; frames: number; duration: number }>(
    (resolve, reject) => {
      let start = Date.now()

      const check = (now: number, frames = 0) => {
        if (props.visible) {
          const duration = now - start
          if (duration > 1200 * s) {
            return resolve({ now, frames, duration: now - start })
          }

          requestAnimationFrame(() => check(Date.now(), frames + 1))
        } else {
          reject()
        }
      }
      check(Date.now())
    }
  )
}

const countdown = async (duration: number) => {
  let value = duration
  try {
    while (value > 0) {
      displaySecond.value = value
      cutting.value = true
      await requestSeconds(1)
      value--
    }
    snapshot()
    cutting.value = false
  } catch (error) {
    console.log('[[ Snapshot ] Countdown Break ]', { value })
    cutting.value = false
  }
}

watch(
  () => props.visible,
  async () => {
    corpedData.value = ''
    corpedUrl.value = ''

    if (props.visible) {
      await delay(200)
      shown.value = true
      const { duration: wait, frames } = await requestSeconds(0.5)
      const fps = (frames / wait) * 1000
      console.log('[[ Snapshot ] Before Countdown ]', {
        wait,
        frames,
        fps
      })
      cuttingWithTransition.value = fps > 7.1
      countdown(CUTTING_NUM)
    } else {
      reset()
    }
  },
  { immediate: true }
)
watch(
  () => props.snapshotData,
  async data => {
    if (props.visible && data?.base64) {
      // console.log('snapshot', data?.base64)
      const originWidth = parseFloat(
        data.width || `${window.document.body.clientWidth}`
      )
      const { x, y, width, height } =
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        previewWrapper.value!.getBoundingClientRect()!
      corpedData.value = await cropBase64Image(
        data.base64,
        x,
        y,
        width,
        height,
        originWidth
      )
      // console.log('croped', corpedData.value)
      corpedUrl.value = corpedData.value //base64ToURL()
    } else {
      corpedData.value = ''
      corpedUrl.value = ''
    }
  }
)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Transition, { name: "layer-apear" }, {
    default: _withCtx(() => [
      (shown.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: _normalizeClass(["snapshot-layer", { 'is-ios': _unref(isIOS) }])
          }, [
            _cache[2] || (_cache[2] = _createElementVNode("div", { class: "snapshot-layer-space-top fill-bottom" }, null, -1)),
            _createElementVNode("div", _hoisted_1, [
              _createElementVNode("div", {
                class: _normalizeClass(["snapshot-layer-title-content", { 'multi-lines': title.value.length > 1 }])
              }, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(title.value, (line, index) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: index,
                    class: "snapshot-layer-title-content-line"
                  }, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(line, (seg, index) => {
                      return (_openBlock(), _createElementBlock("span", {
                        key: index,
                        class: _normalizeClass(seg.typo)
                      }, _toDisplayString(seg.text), 3))
                    }), 128))
                  ]))
                }), 128))
              ], 2)
            ]),
            _cache[3] || (_cache[3] = _createElementVNode("div", { class: "snapshot-layer-space-top fill-top" }, null, -1)),
            _createElementVNode("div", {
              class: _normalizeClass([
          'snapshot-layer-main',
          corpedUrl.value ? 'snapshoted' : undefined,
          isShort.value && 'short'
        ])
            }, [
              _createElementVNode("div", {
                class: _normalizeClass([
            'countdown',
            'ABChanelCorpo-Light',
            'flex-center',
            cutting.value ? 'cutting' : undefined
          ])
              }, [
                _createElementVNode("img", {
                  class: "countdown-anima",
                  src: _unref(namedImages).icon_countdown
                }, null, 8, _hoisted_2),
                _createElementVNode("div", _hoisted_3, [
                  (cuttingWithTransition.value)
                    ? (_openBlock(), _createBlock(_Transition, {
                        key: 0,
                        name: "countdown-trans",
                        mode: "out-in"
                      }, {
                        default: _withCtx(() => [
                          (_openBlock(), _createElementBlock("div", {
                            class: "countdown-number",
                            key: displaySecond.value
                          }, _toDisplayString(displaySecond.value), 1))
                        ]),
                        _: 1
                      }))
                    : (_openBlock(), _createElementBlock("div", _hoisted_4, _toDisplayString(displaySecond.value), 1))
                ])
              ], 2),
              _createElementVNode("div", {
                class: "preview-wrapper",
                ref_key: "previewWrapper",
                ref: previewWrapper
              }, [
                _cache[1] || (_cache[1] = _createElementVNode("div", { class: "placeholder" }, null, -1)),
                _createVNode(_Transition, { name: "preview-img" }, {
                  default: _withCtx(() => [
                    (corpedUrl.value)
                      ? (_openBlock(), _createElementBlock("img", {
                          key: 0,
                          src: corpedUrl.value,
                          class: "preview",
                          draggable: "false",
                          onTouchstart: onTouchStart,
                          onTouchmove: onTouchMove,
                          onTouchend: onTouchEnd,
                          onTouchcancel: onTouchCancel
                        }, null, 40, _hoisted_5))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                })
              ], 512)
            ], 2),
            _cache[4] || (_cache[4] = _createElementVNode("div", { class: "snapshot-layer-space-bottom fill-bottom" }, null, -1)),
            _createElementVNode("div", {
              class: _normalizeClass(["snapshot-layer-footer", { short: isShort.value }])
            }, [
              _createElementVNode("div", _hoisted_6, _toDisplayString(tips.value), 1),
              _createElementVNode("div", {
                class: "snapshot-layer-button PingFangSC-Regular lh-24 font-14",
                onClick: _cache[0] || (_cache[0] = 
//@ts-ignore
(...args) => (_unref(onClose) && _unref(onClose)(...args)))
              }, " 返回 ")
            ], 2),
            _cache[5] || (_cache[5] = _createElementVNode("div", { class: "snapshot-layer-space-bottom fill-top" }, null, -1))
          ], 2))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})